import React from "react";
import PropTypes from "prop-types";
import { Button } from "rsuite";
import {
  getIdTokenClaims,
  trackHeapEvent,
} from "@hoteltradergit/ht-telemetry-ui-lib";
import TrendingUpIcon from "../../../../assets/img/action-center/trending-up.svg";
import TagIcon from "../../../../assets/img/action-center/tag.svg";
import ClockIcon from "../../../../assets/img/action-center/clock.svg";
import CalendarIcon from "../../../../assets/img/action-center/calendar.svg";
import PulseLineIcon from "../../../../assets/img/action-center/pulse-line.svg";
import ChevronRightSmIcon from "../../../../assets/img/action-center/chevron-right-sm.svg";
import useHeapCardViewed from "../../../../hooks/useHeapCardViewed";
import { Can, P } from "../../../../rbac";
import { getCardCategory, CTA_ENUM } from "./llmCardMeta";
import ImpactBand from "./ImpactBand";
import "./LlmCard.scss";

// Band types the v2 cards do not render. "none" has no impact_json to show, and market_gap
// is hidden on business request — the you-vs-benchmark comparison it draws is not wanted on
// the LLM cards. A card left with no band falls through to the design's no-green-box
// treatment. This is the only thing gating ImpactBand's market_gap branch, so removing an
// entry here brings that rendering path back.
const HIDDEN_BANDS = new Set(["none", "market_gap"]);

// The pill's icon, keyed by the category's `icon`. Falls back to trending-up so a
// category added without an icon still renders a pill rather than a broken image.
const CATEGORY_ICON = {
  trending: TrendingUpIcon,
  tag: TagIcon,
  clock: ClockIcon,
  calendar: CalendarIcon,
  pulse: PulseLineIcon,
};

const LlmCard = ({ card, onDismiss, onCta, cardPosition, hotelName }) => {
  const category = getCardCategory(card);
  // Databricks-side analytics segment card engagement by the pipeline's theme
  // (los / availability / alignment / cancellation, …), so every card event
  // carries it — card_type alone only distinguishes the two pill labels. The
  // recommendation_id (ACV2_<CRS>_<THEME>_<YYYYMMDD>) joins the event back to
  // the exact Databricks card row.
  const themeProps = {
    theme: card?.theme || null,
    recommendation_id: card?.recommendationId || null,
  };
  const cardRef = useHeapCardViewed(
    `llm_${category.slug}`,
    cardPosition,
    hotelName,
    themeProps
  );

  const impact = card?.impact;
  const hasImpact =
    card?.impactBandType &&
    !HIDDEN_BANDS.has(card.impactBandType) &&
    Boolean(impact);
  const metrics = (card?.metrics || []).slice(0, 2);

  const heapProps = {
    card_type: `llm_${category.slug}`,
    card_position: cardPosition,
    page_section: "action_center",
    hotel_name: hotelName || null,
    cta: card?.cta || null,
    cta_enum: card?.ctaEnum ?? null,
    rank: card?.rank ?? null,
    user_type: getIdTokenClaims()?.["custom:userType"] || null,
    ...themeProps,
  };

  const handleMaybeLater = () => {
    trackHeapEvent("Recommendation Card Dismissed", heapProps);
    if (onDismiss) onDismiss();
  };

  const handleCta = () => {
    trackHeapEvent("Recommendation Card CTA Clicked", heapProps);
    if (onCta) onCta(card);
  };

  return (
    <div ref={cardRef} className="llm-card">
      <div className={`llm-card-badge llm-card-badge--${category.variant}`}>
        <img
          src={CATEGORY_ICON[category.icon] || TrendingUpIcon}
          alt=""
          className="badge-icon"
        />
        <span>{category.label}</span>
      </div>

      <h3 className="llm-card-title">{card?.headline}</h3>

      {/* The body is shown in full: the copy the pipeline writes is already sized for the
          card, so a More/Less toggle only hid the end of a sentence behind a click. */}
      <p className="llm-card-description">{card?.body}</p>

      <div className="llm-card-data">
        {/* The design's "No green box" variant (Figma 4805:25798): where a card has no impact
            band, a 1px rule takes its place so the metrics still read as their own block
            rather than running straight on from the body copy. Gated on there being metrics
            to separate — a lone rule under the body would just be a stray line. */}
        {hasImpact ? (
          <ImpactBand type={card.impactBandType} impact={impact} />
        ) : (
          metrics.length > 0 && <hr className="llm-card-divider" />
        )}

        {metrics.length > 0 && (
          <div className="llm-card-comparison">
            {metrics.map((metric, index) => (
              <div className="comparison-row" key={`${metric.label}-${index}`}>
                <div className="comparison-label">
                  <span
                    className={`comparison-dot comparison-dot--${
                      index === 0 ? "primary" : "secondary"
                    }`}
                  />
                  <span>{metric.label}</span>
                </div>
                <div className="comparison-values">
                  <span className="comparison-value">{metric.value}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="llm-card-actions">
        <div className="llm-card-actions-left">
          <Button
            id="action-llm-later"
            appearance="link"
            className="maybe-later-btn"
            onClick={handleMaybeLater}
          >
            Maybe Later
          </Button>
        </div>
        {card?.cta &&
          (() => {
            const ctaButton = (
              <Button
                id="action-llm-cta"
                appearance="ghost"
                className="llm-cta-btn"
                onClick={handleCta}
              >
                {card.cta}{" "}
                <img src={ChevronRightSmIcon} alt="" className="btn-chevron" />
              </Button>
            );
            // Create Promo opens the promo drawer, so gate it on the same
            // permission the v1 PromoCard uses; other CTAs just navigate.
            return card.ctaEnum === CTA_ENUM.CREATE_PROMO ? (
              <Can permission={P.Home.ActionCenter.CreatePromo}>
                {ctaButton}
              </Can>
            ) : (
              ctaButton
            );
          })()}
      </div>
    </div>
  );
};

LlmCard.propTypes = {
  card: PropTypes.shape({
    rank: PropTypes.number,
    cardType: PropTypes.string,
    headline: PropTypes.string,
    body: PropTypes.string,
    metrics: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    cta: PropTypes.string,
    ctaEnum: PropTypes.number,
    impactBandType: PropTypes.string,
    impact: PropTypes.object,
    theme: PropTypes.string,
    segment: PropTypes.string,
    recommendationId: PropTypes.string,
  }).isRequired,
  onDismiss: PropTypes.func,
  onCta: PropTypes.func,
  cardPosition: PropTypes.number,
  hotelName: PropTypes.string,
};

export default LlmCard;
